import React, { useState } from 'react';
import { AlertTriangle, Upload, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { toast } from 'sonner';

const DisputeFilingModal = ({ delivery, onClose, onSuccess }) => {
  const [submitting, setSubmitting] = useState(false);
  const [disputeData, setDisputeData] = useState({
    dispute_type: 'non_delivery',
    description: '',
    evidence_urls: []
  });

  const disputeTypes = [
    { value: 'non_delivery', label: 'Package Not Delivered', description: 'Package was not delivered to recipient' },
    { value: 'damaged_package', label: 'Damaged Package', description: 'Package arrived damaged or broken' },
    { value: 'wrong_item', label: 'Wrong Item', description: 'Wrong package was delivered' },
    { value: 'driver_issue', label: 'Driver Issue', description: 'Issue with driver behavior or service' },
    { value: 'customer_issue', label: 'Customer Issue', description: 'Issue with customer behavior' },
    { value: 'payment_issue', label: 'Payment Issue', description: 'Problem with payment or charges' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!disputeData.description.trim()) {
      toast.error('Please provide a detailed description');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/pickup/deliveries/${delivery.id}/dispute`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            delivery_id: delivery.id,
            ...disputeData
          })
        }
      );

      if (!response.ok) throw new Error('Failed to file dispute');

      const data = await response.json();
      toast.success('Dispute filed successfully');
      onSuccess(data);
      onClose();
    } catch (error) {
      console.error('File dispute error:', error);
      toast.error('Failed to file dispute. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <CardTitle>File a Dispute</CardTitle>
                <CardDescription>
                  Delivery #{delivery.delivery_code} - {delivery.pickup_city} → {delivery.destination_city}
                </CardDescription>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="h-5 w-5" />
            </Button>
          </div>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Dispute Type */}
            <div className="space-y-3">
              <Label>Select Issue Type *</Label>
              <div className="grid md:grid-cols-2 gap-3">
                {disputeTypes.map((type) => (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => setDisputeData({ ...disputeData, dispute_type: type.value })}
                    className={`p-4 text-left border-2 rounded-lg transition-colors ${
                      disputeData.dispute_type === type.value
                        ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <div className="font-medium">{type.label}</div>
                    <div className="text-xs text-muted-foreground mt-1">{type.description}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label>Detailed Description *</Label>
              <textarea
                className="w-full min-h-[120px] px-3 py-2 border rounded-md"
                placeholder="Please provide detailed information about the issue. Include dates, times, and any relevant details..."
                value={disputeData.description}
                onChange={(e) => setDisputeData({ ...disputeData, description: e.target.value })}
                required
              />
              <p className="text-xs text-muted-foreground">
                Be as specific as possible. This will help us resolve your dispute faster.
              </p>
            </div>

            {/* Evidence URLs */}
            <div className="space-y-2">
              <Label>Evidence (Optional)</Label>
              <div className="space-y-2">
                <Input
                  type="text"
                  placeholder="Paste photo/document URL (e.g., from Google Drive, Dropbox)"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const url = e.target.value.trim();
                      if (url && !disputeData.evidence_urls.includes(url)) {
                        setDisputeData({
                          ...disputeData,
                          evidence_urls: [...disputeData.evidence_urls, url]
                        });
                        e.target.value = '';
                      }
                    }
                  }}
                />
                <p className="text-xs text-muted-foreground">
                  Press Enter to add multiple URLs. Upload photos to Google Drive/Dropbox and paste the link.
                </p>
              </div>

              {/* Evidence List */}
              {disputeData.evidence_urls.length > 0 && (
                <div className="space-y-2 mt-3">
                  {disputeData.evidence_urls.map((url, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded"
                    >
                      <span className="text-sm truncate flex-1">{url}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setDisputeData({
                            ...disputeData,
                            evidence_urls: disputeData.evidence_urls.filter((_, i) => i !== index)
                          });
                        }}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Warning */}
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <p className="text-sm text-blue-800 dark:text-blue-400">
                <strong>Note:</strong> Filing a false dispute may result in account suspension. 
                Our team will review your dispute within 1-2 business days and may contact you for additional information.
              </p>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <Button type="button" variant="outline" onClick={onClose} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} className="flex-1">
                {submitting ? 'Submitting...' : 'Submit Dispute'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default DisputeFilingModal;
